import { Decimal } from 'decimal.js';

/**
 * Calculs monétaires exacts. Entrées et sorties en chaînes décimales (« 12.50 »).
 * Arrondi au centime, demi supérieur (ROUND_HALF_UP), ligne par ligne puis somme.
 */
const D = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP });

export interface LineAmountsInput {
  quantity: string;
  unitPriceHt: string;
  vatRate: string;
}

export interface LineAmounts {
  totalHt: string;
  totalVat: string;
  totalTtc: string;
}

export interface Totals {
  totalHt: string;
  totalVat: string;
  totalTtc: string;
}

const cents = (d: Decimal) => d.toDecimalPlaces(2, Decimal.ROUND_HALF_UP);

export function lineAmounts(line: LineAmountsInput): LineAmounts {
  const ht = cents(new D(line.quantity).mul(line.unitPriceHt));
  const vat = cents(ht.mul(line.vatRate).div(100));
  return { totalHt: ht.toFixed(2), totalVat: vat.toFixed(2), totalTtc: ht.add(vat).toFixed(2) };
}

export function sumTotals(lines: readonly LineAmountsInput[]): Totals {
  let ht = new D(0);
  let vat = new D(0);
  for (const line of lines) {
    const a = lineAmounts(line);
    ht = ht.add(a.totalHt);
    vat = vat.add(a.totalVat);
  }
  return { totalHt: ht.toFixed(2), totalVat: vat.toFixed(2), totalTtc: ht.add(vat).toFixed(2) };
}

export function addMoney(...values: string[]): string {
  return values.reduce((acc, v) => acc.add(v), new D(0)).toFixed(2);
}

export function multiplyMoney(value: string, factor: string | number): string {
  return cents(new D(value).mul(factor)).toFixed(2);
}

export function compareMoney(a: string, b: string): number {
  return new D(a).comparedTo(b);
}

const NBSP = String.fromCharCode(0xa0);
const NARROW_NBSP = String.fromCharCode(0x202f);

/** Formatage d'affichage (fr-FR). Ne sert qu'à l'affichage, jamais aux calculs. */
export function formatEuros(value: string): string {
  const [int = '0', dec = '00'] = new D(value).toFixed(2).split('.');
  const negative = int.startsWith('-');
  const digits = negative ? int.slice(1) : int;
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, NARROW_NBSP);
  return `${negative ? '-' : ''}${grouped},${dec}${NBSP}€`;
}
